/**
 * Sarlavhadagi «Maxsus imkoniyatlar»: vaqt (Toshkent), til tanlash va ko'rish imkoniyati
 * cheklanganlar paneli — ko'rinish rejimi, shrift o'lchami, masshtab, ekran suhandoni
 * (brauzerning ovozli o'qishi) va Google tarjimon. Tanlov `localStorage` da saqlanadi.
 */
import { useEffect, useRef, useState } from "react";

import { LANGS, useI18n } from "@/shared/i18n";
import { usePrefs } from "@/shared/lib/prefs";
import type { Theme } from "@/shared/lib/prefs";
import { Icon } from "@/shared/ui/Icon";

/* ----------------------------------------------------------------------- vaqt */
function useTashkentClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tashkent",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(now);
  const date = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tashkent",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
    .format(now)
    .replace(/\//g, ".");
  return { time, date };
}

export function HeaderClock() {
  const { time, date } = useTashkentClock();
  return (
    <span className="hdr-clock" aria-label={`${time}, ${date}`}>
      <b>{time} (GMT+5)</b>
      <span>{date}</span>
    </span>
  );
}

/* ---------------------------------------------------------------------- til */
export function LangMenu() {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const current = LANGS.find((item) => item.code === lang) ?? LANGS[0];

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="hdr-lang" ref={root}>
      <button
        type="button"
        className="hdr-lang-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("pref.language")}
        onClick={() => setOpen((value) => !value)}
      >
        {current.label}
      </button>
      {open ? (
        <ul className="hdr-lang-list" role="listbox">
          {LANGS.map((item) => (
            <li key={item.code}>
              <button
                type="button"
                role="option"
                aria-selected={item.code === lang}
                lang={item.code}
                onClick={() => {
                  setLang(item.code);
                  setOpen(false);
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* --------------------------------------------------------------- sozlamalar */
const FONT_STEPS = 9; // 0…8, 4 = 100 %
const ZOOM_VALUES = [80, 90, 100, 110, 120, 130, 140, 150, 160];
const TRANSLATE_LANGS: Array<[string, string]> = [
  ["en", "English"],
  ["ru", "Русский"],
  ["uz", "O'zbekcha"],
  ["tr", "Türkçe"],
  ["zh-CN", "中文"],
  ["ar", "العربية"],
  ["de", "Deutsch"],
  ["fr", "Français"],
];

function load(key: string, fallback: number): number {
  try {
    const value = Number(localStorage.getItem(key));
    return Number.isFinite(value) && localStorage.getItem(key) !== null ? value : fallback;
  } catch {
    return fallback;
  }
}
function save(key: string, value: string | number) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* saqlab bo'lmadi */
  }
}

/** Sahifa ochilganda saqlangan shrift, masshtab va rasmlar sozlamalarini qo'llaydi. */
function applyDisplay(font: number, zoom: number, imagesOff: boolean) {
  const root = document.documentElement;
  root.style.fontSize = `${80 + font * 5}%`;
  (root.style as CSSStyleDeclaration & { zoom: string }).zoom = zoom === 100 ? "" : `${zoom}%`;
  if (imagesOff) root.dataset.images = "off";
  else delete root.dataset.images;
}

function speak(text: string, gender: "male" | "female", lang: string) {
  if (!("speechSynthesis" in window) || !text.trim()) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text.trim().slice(0, 600));
  utterance.lang = lang === "uz" ? "uz-UZ" : lang === "ru" ? "ru-RU" : "en-GB";
  const voices = window.speechSynthesis.getVoices().filter((v) => v.lang.startsWith(lang));
  const female = /female|woman|zira|samantha|anna|elena|svetlana|ayol/i;
  const pick = voices.find((v) => (gender === "female") === female.test(v.name)) ?? voices[0];
  if (pick) utterance.voice = pick;
  utterance.pitch = gender === "female" ? 1.25 : 0.8;
  window.speechSynthesis.speak(utterance);
}

export function A11yButton() {
  const { t, lang } = useI18n();
  const { theme, setTheme } = usePrefs();
  const [open, setOpen] = useState(false);
  const [font, setFont] = useState(() => load("sf.a11y.font", 4));
  const [zoom, setZoom] = useState(() => load("sf.a11y.zoom", 100));
  const [imagesOff, setImagesOff] = useState(() => load("sf.a11y.images", 0) === 1);
  const [reader, setReader] = useState(() => load("sf.a11y.reader", 0) === 1);
  const [gender, setGender] = useState<"male" | "female">(() =>
    load("sf.a11y.voice", 0) === 1 ? "female" : "male",
  );

  useEffect(() => {
    applyDisplay(font, zoom, imagesOff);
    save("sf.a11y.font", font);
    save("sf.a11y.zoom", zoom);
    save("sf.a11y.images", imagesOff ? 1 : 0);
  }, [font, zoom, imagesOff]);

  useEffect(() => {
    save("sf.a11y.reader", reader ? 1 : 0);
    save("sf.a11y.voice", gender === "female" ? 1 : 0);
    if (!reader) {
      window.speechSynthesis?.cancel();
      return undefined;
    }
    const onClick = (event: MouseEvent) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>(
        "h1,h2,h3,h4,p,li,a,button,label,summary,td,th,span,b",
      );
      if (target && !target.closest(".a11y-panel")) speak(target.innerText || target.textContent || "", gender, lang);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [reader, gender, lang]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const modes: Array<{ key: string; label: string; active: boolean; apply: () => void }> = [
    { key: "light", label: t("pref.theme.light"), active: theme === "light" && !imagesOff, apply: () => { setTheme("light"); setImagesOff(false); } },
    { key: "dark", label: t("pref.theme.dark"), active: theme === "dark" && !imagesOff, apply: () => { setTheme("dark"); setImagesOff(false); } },
    { key: "hc", label: t("pref.theme.hc"), active: theme === "hc" && !imagesOff, apply: () => { setTheme("hc" as Theme); setImagesOff(false); } },
    { key: "img", label: t("a11y.imagesOff"), active: imagesOff, apply: () => setImagesOff((value) => !value) },
  ];

  return (
    <>
      <button
        type="button"
        className="hdr-eye"
        aria-label={t("a11y.title")}
        title={t("a11y.title")}
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <Icon name="eye" size={22} />
      </button>

      {open ? <button type="button" className="a11y-scrim" aria-label={t("action.close")} onClick={() => setOpen(false)} /> : null}
      <aside className={`a11y-panel${open ? " is-open" : ""}`} aria-hidden={!open} aria-label={t("a11y.title")}>
        <header>
          <button type="button" className="a11y-close" aria-label={t("action.close")} onClick={() => setOpen(false)}>
            <Icon name="close" size={26} />
          </button>
          <h2>{t("a11y.title")}</h2>
        </header>

        <div className="a11y-modes" role="group" aria-label={t("pref.theme")}>
          {modes.map((mode) => (
            <button
              key={mode.key}
              type="button"
              className={mode.active ? "is-on" : ""}
              aria-pressed={mode.active}
              aria-label={mode.label}
              title={mode.label}
              onClick={mode.apply}
            >
              <Icon name={mode.key === "img" ? "camera" : mode.key === "dark" ? "moon" : mode.key === "hc" ? "contrast" : "sun"} size={26} />
            </button>
          ))}
        </div>

        <section>
          <label htmlFor="a11y-font">{t("a11y.font")}</label>
          <input id="a11y-font" type="range" min={0} max={FONT_STEPS - 1} step={1} value={font} onChange={(e) => setFont(Number(e.target.value))} />
          <output>{Math.round((font / (FONT_STEPS - 1)) * 100)}%</output>
        </section>

        <section>
          <label htmlFor="a11y-zoom">{t("a11y.zoom")}</label>
          <input id="a11y-zoom" type="range" min={0} max={ZOOM_VALUES.length - 1} step={1} value={ZOOM_VALUES.indexOf(zoom)} onChange={(e) => setZoom(ZOOM_VALUES[Number(e.target.value)])} />
          <output>{zoom}%</output>
        </section>

        <section>
          <div className="a11y-row">
            <span>{t("a11y.reader")}</span>
          </div>
          <label className="a11y-check">
            <input type="checkbox" checked={reader} onChange={(e) => setReader(e.target.checked)} />
            {t("a11y.readerOn")}
          </label>
          <div className="a11y-radios" role="radiogroup" aria-label={t("a11y.reader")}>
            <label>
              <input type="radio" name="a11y-voice" checked={gender === "male"} onChange={() => setGender("male")} />
              {t("a11y.male")}
            </label>
            <label>
              <input type="radio" name="a11y-voice" checked={gender === "female"} onChange={() => setGender("female")} />
              {t("a11y.female")}
            </label>
          </div>
          <p className="a11y-hint">{t("a11y.readerHint")}</p>
        </section>

        <section>
          <label htmlFor="a11y-translate">{t("a11y.translate")}</label>
          <select
            id="a11y-translate"
            defaultValue=""
            onChange={(e) => {
              if (!e.target.value) return;
              window.open(
                `https://translate.google.com/translate?sl=auto&tl=${e.target.value}&u=${encodeURIComponent(window.location.href)}`,
                "_blank",
                "noopener,noreferrer",
              );
              e.target.value = "";
            }}
          >
            <option value="">{t("a11y.pickLang")}</option>
            {TRANSLATE_LANGS.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </section>

        <button
          type="button"
          className="btn btn-outline btn-block"
          onClick={() => {
            setFont(4);
            setZoom(100);
            setImagesOff(false);
            setTheme("light");
            setReader(false);
          }}
        >
          <Icon name="refresh" size={16} />
          {t("a11y.reset")}
        </button>
      </aside>
    </>
  );
}
